import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import CategoryChips, { buildGroups } from '../components/CategoryChips';

const Results = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category_id: '',   // a category id, 'none' for uncategorised, '' for all
    subcategory: '',
    session_id: '',
    quiz_id: '',
    candidate_name: '',
    status: '',
    date_from: '',
    date_to: ''
  });
  const [filterOptions, setFilterOptions] = useState({ quizzes: [], sessions: [], groups: [] });
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [emailConfigured, setEmailConfigured] = useState(false);
  const [sendingEmail, setSendingEmail] = useState({});
  const [selectedForEmail, setSelectedForEmail] = useState([]);
  const [sendingBulk, setSendingBulk] = useState(false);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const response = await api.get('/results/filters');
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
    }
  }, []);

  const fetchResults = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      params.append('page', pagination.page);
      params.append('limit', pagination.limit);

      const [resultsRes, statsRes] = await Promise.all([
        api.get(`/results?${params.toString()}`),
        api.get(`/results/stats?${params.toString()}`)
      ]);

      setResults(resultsRes.data.results);
      setPagination(resultsRes.data.pagination);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch results:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  // Check if email is configured
  useEffect(() => {
    const checkEmailStatus = async () => {
      try {
        const response = await api.get('/exam/email/status');
        setEmailConfigured(response.data.configured);
      } catch (error) {
        console.log('Email not configured');
      }
    };
    checkEmailStatus();
  }, []);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      category_id: '',
      subcategory: '',
      session_id: '',
      quiz_id: '',
      candidate_name: '',
      status: '',
      date_from: '',
      date_to: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // "26 Oct, 5:46 PM" -- the year only when it is not this year, so the column
  // stays on one line.
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    const day = d.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      ...(d.getFullYear() !== new Date().getFullYear() && { year: 'numeric' })
    });
    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return `${day}, ${time}`;
  };

  const getStatusBadge = (result) => {
    if (result.attempt_status === 'submitted') {
      return <span className="badge badge-warning">Pending Review</span>;
    }
    if (result.passed) {
      return <span className="badge badge-success">Passed</span>;
    }
    return <span className="badge badge-danger">Failed</span>;
  };

  // Chips come from server-side counts so they reflect every result, not just
  // the page currently loaded.
  const keyOfGroup = g => (g.category_id === null || g.category_id === undefined ? 'none' : String(g.category_id));
  const chipGroups = buildGroups(filterOptions.groups || [], {
    keyOf: keyOfGroup,
    labelOf: g => g.category_name || 'Uncategorised',
    subOf: g => g.subcategory,
    weight: g => g.count
  });
  const chipTotal = (filterOptions.groups || []).reduce((n, g) => n + g.count, 0);

  const inChosenGroup = item => {
    if (filters.category_id && keyOfGroup(item) !== filters.category_id) return false;
    if (filters.subcategory && item.subcategory !== filters.subcategory) return false;
    return true;
  };
  const quizOptions = filterOptions.quizzes.filter(inChosenGroup);
  const sessionOptions = filterOptions.sessions.filter(inChosenGroup);

  // Picking a group clears a quiz or session choice that no longer belongs to it.
  const handleCategoryChange = (categoryKey, sub) => {
    setFilters(prev => {
      const next = { ...prev, category_id: categoryKey, subcategory: sub };
      const fits = item => (!categoryKey || keyOfGroup(item) === categoryKey)
        && (!sub || item.subcategory === sub);
      const quiz = filterOptions.quizzes.find(q => String(q.id) === String(prev.quiz_id));
      const session = filterOptions.sessions.find(x => String(x.id) === String(prev.session_id));
      if (quiz && !fits(quiz)) next.quiz_id = '';
      if (session && !fits(session)) next.session_id = '';
      return next;
    });
    setPagination(prev => ({ ...prev, page: 1 }));
    setSelectedForEmail([]);
  };

  // Only submitted/graded attempts can be mailed; in-progress ones have no score.
  const mailable = (r) => r.status !== 'in_progress' && r.candidate_email;

  // Extra details vary per session, so the columns are derived from whatever the
  // current result set actually contains rather than being fixed.
  const introLabels = Array.from(
    results.reduce((set, r) => {
      Object.keys(r.intro_answers || {}).forEach(k => set.add(k));
      return set;
    }, new Set())
  ).sort();

  const toggleSelect = (attemptId) => {
    setSelectedForEmail(prev =>
      prev.includes(attemptId) ? prev.filter(x => x !== attemptId) : [...prev, attemptId]
    );
  };

  const toggleSelectAll = () => {
    const ids = results.filter(mailable).map(r => r.attempt_id);
    setSelectedForEmail(selectedForEmail.length === ids.length ? [] : ids);
  };

  const handleSendBulkResults = async () => {
    if (selectedForEmail.length === 0) return;
    if (!window.confirm(`Send results email to ${selectedForEmail.length} candidate(s)?`)) return;

    setSendingBulk(true);
    try {
      const response = await api.post('/results/send-emails', {
        attemptIds: selectedForEmail,
        resultBaseUrl: window.location.origin
      });
      const { sent = [], failed = [], skipped = [] } = response.data;
      const lines = [`Sent ${sent.length} of ${selectedForEmail.length} email(s).`];
      if (failed.length) {
        lines.push('', 'Failed:');
        failed.forEach(f => lines.push(`  ${f.candidate_name}: ${f.reason}`));
      }
      if (skipped.length) {
        lines.push('', `Skipped ${skipped.length} (not available to you, or still in progress).`);
      }
      alert(lines.join('\n'));
      setSelectedForEmail([]);
      fetchResults();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to send emails');
    } finally {
      setSendingBulk(false);
    }
  };

  const handleSendResultsEmail = async (attemptId, candidateEmail) => {
    if (!window.confirm(`Send results email to ${candidateEmail}?`)) {
      return;
    }

    setSendingEmail(prev => ({ ...prev, [attemptId]: true }));
    try {
      // Authenticated route: the public /exam/result/... endpoint now needs the
      // candidate's own token, which an examiner does not hold.
      const response = await api.post('/results/send-emails', {
        attemptIds: [attemptId],
        resultBaseUrl: window.location.origin
      });
      const failed = response.data.failed || [];
      if (failed.length) throw new Error(failed[0].reason);
      alert(`Results email sent to ${candidateEmail}`);
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Failed to send email');
    } finally {
      setSendingEmail(prev => ({ ...prev, [attemptId]: false }));
    }
  };

  return (
    <div>
      <div className="flex flex-between flex-center mb-5">
        <div>
          <h1>Exam Results</h1>
          <p className="text-muted">Search and filter exam results across all sessions</p>
        </div>
      </div>

      <CategoryChips
        groups={chipGroups}
        total={chipTotal}
        category={filters.category_id}
        subcategory={filters.subcategory}
        onChange={handleCategoryChange}
      />

      {/* Stats Summary */}
      {stats && (
        <div className="card mb-4" style={{ padding: '16px' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: '16px',
            textAlign: 'center'
          }}>
            <div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#374151' }}>
                {stats.total_attempts || 0}
              </div>
              <div className="text-muted text-sm">Total Attempts</div>
            </div>
            <div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#10b981' }}>
                {stats.passed_count || 0}
              </div>
              <div className="text-muted text-sm">Passed</div>
            </div>
            <div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#ef4444' }}>
                {stats.failed_count || 0}
              </div>
              <div className="text-muted text-sm">Failed</div>
            </div>
            <div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#f59e0b' }}>
                {stats.pending_review_count || 0}
              </div>
              <div className="text-muted text-sm">Pending Review</div>
            </div>
            <div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#3b82f6' }}>
                {stats.avg_percentage || 0}%
              </div>
              <div className="text-muted text-sm">Avg Score</div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card mb-4" style={{ padding: '16px' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '12px'
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#6b7280' }}>
              Quiz
            </label>
            <select
              value={filters.quiz_id}
              onChange={e => handleFilterChange('quiz_id', e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <option value="">All Quizzes</option>
              {quizOptions.map(q => (
                <option key={q.id} value={q.id}>{q.title}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#6b7280' }}>
              Session
            </label>
            <select
              value={filters.session_id}
              onChange={e => handleFilterChange('session_id', e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <option value="">All Sessions</option>
              {sessionOptions.map(s => (
                <option key={s.id} value={s.id}>{s.session_name} ({s.quiz_title})</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#6b7280' }}>
              Candidate Name
            </label>
            <input
              type="text"
              value={filters.candidate_name}
              onChange={e => handleFilterChange('candidate_name', e.target.value)}
              placeholder="Search by name..."
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#6b7280' }}>
              Status
            </label>
            <select
              value={filters.status}
              onChange={e => handleFilterChange('status', e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <option value="">All Statuses</option>
              <option value="passed">Passed</option>
              <option value="failed">Failed</option>
              <option value="pending_review">Pending Review</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#6b7280' }}>
              From Date
            </label>
            <input
              type="date"
              value={filters.date_from}
              onChange={e => handleFilterChange('date_from', e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#6b7280' }}>
              To Date
            </label>
            <input
              type="date"
              value={filters.date_to}
              onChange={e => handleFilterChange('date_to', e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>

        {(filters.quiz_id || filters.session_id || filters.candidate_name || filters.status || filters.date_from || filters.date_to) && (
          <div style={{ marginTop: '12px', textAlign: 'right' }}>
            <button
              onClick={clearFilters}
              style={{
                padding: '6px 12px',
                background: '#f3f4f6',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px'
              }}
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Bulk actions */}
      {emailConfigured && selectedForEmail.length > 0 && (
        <div className="card" style={{
          marginBottom: '16px', padding: '12px 16px', display: 'flex',
          alignItems: 'center', justifyContent: 'space-between',
          backgroundColor: '#fff7ed', border: '1px solid #fed7aa'
        }}>
          <span style={{ fontSize: '14px' }}>
            <strong>{selectedForEmail.length}</strong> candidate{selectedForEmail.length === 1 ? '' : 's'} selected
          </span>
          <div className="flex gap-2">
            <button className="btn btn-sm btn-secondary" onClick={() => setSelectedForEmail([])} disabled={sendingBulk}>
              Clear
            </button>
            <button className="btn btn-sm btn-primary" onClick={handleSendBulkResults} disabled={sendingBulk}>
              {sendingBulk ? 'Sending...' : `Send Results (${selectedForEmail.length})`}
            </button>
          </div>
        </div>
      )}

      {/* Results Table */}
      <div className="card">
        {loading ? (
          <div className="text-center" style={{ padding: '40px' }}>Loading results...</div>
        ) : results.length === 0 ? (
          <div className="empty-state">
            <h3>No results found</h3>
            <p>Try adjusting your filters or wait for candidates to complete exams</p>
          </div>
        ) : (
          <>
            {/* Scrolls sideways inside the card on narrow screens instead of
                spilling past it; the extra-detail columns vary per session. */}
            <div className="table-scroll">
            <table className="table results-table">
              <thead>
                <tr>
                  {emailConfigured && (
                    <th style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        checked={selectedForEmail.length > 0 && selectedForEmail.length === results.filter(mailable).length}
                        onChange={toggleSelectAll}
                        title="Select all candidates with results"
                        style={{ width: '16px', height: '16px' }}
                      />
                    </th>
                  )}
                  <th>Candidate</th>
                  {introLabels.length > 0 && <th>Details</th>}
                  <th>Quiz / Session</th>
                  <th>Result</th>
                  <th className="sticky-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {results.map(result => (
                  <tr key={result.attempt_id}>
                    {emailConfigured && (
                      <td>
                        {mailable(result) && (
                          <input
                            type="checkbox"
                            checked={selectedForEmail.includes(result.attempt_id)}
                            onChange={() => toggleSelect(result.attempt_id)}
                            style={{ width: '16px', height: '16px' }}
                          />
                        )}
                      </td>
                    )}
                    <td className="candidate-col">
                      <strong className="truncate" style={{ display: 'block' }} title={result.candidate_name}>
                        {result.candidate_name}
                      </strong>
                      <div className="text-muted text-sm truncate" title={result.candidate_email}>
                        {result.candidate_email}
                      </div>
                    </td>
                    {/* One compact column rather than one per detail, so the
                        table fits a laptop screen whatever a session asked. */}
                    {introLabels.length > 0 && (
                      <td className="detail-col">
                        {Object.keys(result.intro_answers || {}).length === 0
                          ? <span className="text-muted">—</span>
                          : introLabels.map(l => {
                          const value = (result.intro_answers || {})[l];
                          return (
                            <div key={l} className="truncate" title={value ? `${l}: ${value}` : l}>
                              <span className="text-muted">{l}:</span> {value || '—'}
                            </div>
                          );
                        })}
                      </td>
                    )}
                    <td className="quiz-col">
                      <div className="clamp-2" title={result.quiz_title} style={{ fontSize: '14px', fontWeight: 500 }}>
                        {result.quiz_title}
                      </div>
                      <Link
                        to={`/sessions/${result.session_id}`}
                        className="truncate"
                        title={result.session_name}
                        style={{ display: 'block', fontSize: '12px', color: '#f97316', textDecoration: 'none' }}
                      >
                        {result.session_name}
                      </Link>
                      {result.category_name && (
                        <div className="truncate" style={{ fontSize: '11px', color: '#6d28d9' }}>
                          {result.category_name}{result.subcategory ? ` › ${result.subcategory}` : ''}
                        </div>
                      )}
                    </td>
                    <td className="nowrap">
                      <span style={{
                        fontSize: '15px',
                        fontWeight: '600',
                        color: result.passed ? '#10b981' : result.attempt_status === 'submitted' ? '#f59e0b' : '#ef4444'
                      }}>
                        {result.percentage !== null ? `${parseFloat(result.percentage)}%` : '-'}
                      </span>
                      {result.score !== null && <span className="text-muted text-sm"> · {result.score} pts</span>}
                      <div style={{ marginTop: '4px' }}>{getStatusBadge(result)}</div>
                      <div className="text-muted" style={{ fontSize: '12px', marginTop: '4px' }}>
                        {formatDate(result.submitted_at)}
                      </div>
                    </td>
                    <td className="nowrap sticky-right">
                      <div className="flex gap-2">
                        <Link
                          to={`/quizzes/${result.quiz_id}/results?attempt=${result.attempt_id}&from=results`}
                          className="btn btn-secondary btn-sm"
                        >
                          View
                        </Link>
                        {emailConfigured && (
                          <button
                            className="btn btn-success btn-sm"
                            onClick={() => handleSendResultsEmail(result.attempt_id, result.candidate_email)}
                            disabled={sendingEmail[result.attempt_id]}
                            title="Send results email to candidate"
                          >
                            {sendingEmail[result.attempt_id] ? '...' : 'Email'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px',
                borderTop: '1px solid #e5e7eb'
              }}>
                <span className="text-muted text-sm">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                    className="btn btn-secondary btn-sm"
                    style={{ opacity: pagination.page === 1 ? 0.5 : 1 }}
                  >
                    Previous
                  </button>
                  <span style={{ padding: '6px 12px', fontSize: '14px' }}>
                    Page {pagination.page} of {pagination.pages}
                  </span>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page === pagination.pages}
                    className="btn btn-secondary btn-sm"
                    style={{ opacity: pagination.page === pagination.pages ? 0.5 : 1 }}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Results;
